import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import InviteTrainerModal from './InviteTrainerModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import {
    canRemoveTrainer,
    canInviteTrainers,
    getRoleDisplayName
} from '../../utils/trainerRoleUtils';
import '../../styles/TrainersStyles.css';

const TrainersList = () => {
    const { t } = useTranslation(['school', 'common']);
    const { trainer } = useContext(TrainerContext);
    const [trainers, setTrainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [trainerToDelete, setTrainerToDelete] = useState(null);

    useEffect(() => {
        if (!trainer?.schoolId) {
            setLoading(false);
            return;
        }
        const token = localStorage.getItem('jwt');

        axios
            .get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setTrainers(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError(t('trainersList.fetchError'));
                setLoading(false);
            });
    }, [trainer?.schoolId]);

    const formatAddress = (address) => {
        if (!address) return t('trainersList.noData');
        return `${address.street || ''} ${address.buildingNumber || ''}${
            address.apartmentNumber ? '/' + address.apartmentNumber : ''
        }, ${address.postalCode || ''} ${address.city || ''}, ${
            address.country || ''
        }`.trim();
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const openDeleteModal = (trainerData) => {
        setTrainerToDelete(trainerData);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setTrainerToDelete(null);
    };

    const handleDeleteTrainer = async () => {
        if (!trainerToDelete) return;

        const token = localStorage.getItem('jwt');

        try {
            await axios.delete(`/api/v1/trainer/remove-from-school/${trainerToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setTrainers((prevTrainers) =>
                prevTrainers.filter((t) => t.id !== trainerToDelete.id)
            );

            closeDeleteModal();
        } catch (err) {
            console.error('Error deleting trainer:', err);
            setError(t('trainersList.deleteError'));
            closeDeleteModal();
        }
    };

    return (
        <div className="trainers-page-container">
            <div className="trainers-header-container">
                {canInviteTrainers(trainer?.trainerSchoolRole) && (
                    <button
                        className="trainers-invite-button"
                        onClick={openModal}
                    >
                        {t('trainersList.addTrainer')}
                    </button>
                )}
            </div>

            {loading ? (
                <p className="trainers-loading-text">{t('common:loading')}</p>
            ) : error ? (
                <p className="trainers-error-message">{error}</p>
            ) : (
                <>
                    <div className="trainers-table-container">
                        <table className="trainers-table">
                            <thead>
                            <tr>
                                <th>{t('trainersList.fullName')}</th>
                                <th>{t('trainersList.email')}</th>
                                <th>{t('trainersList.phone')}</th>
                                <th>{t('trainersList.address')}</th>
                                <th>{t('trainersList.role')}</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {trainers.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="trainers-no-data-row">{t('trainersList.noTrainers')}</td>
                                </tr>
                            ) : (
                                trainers.map((t_item) => {
                                    const fullName =
                                        t_item.firstName && t_item.lastName
                                            ? `${t_item.firstName} ${t_item.lastName}`
                                            : t_item.username;

                                    const isCurrentUser = trainer?.id === t_item.id;
                                    const canRemove = !isCurrentUser && canRemoveTrainer(trainer?.trainerSchoolRole, t_item.trainerSchoolRole);

                                    return (
                                        <tr key={t_item.id} className="trainers-clickable-row">
                                            <td className="trainers-centered-column">
                                                <strong>{fullName}</strong>
                                                {isCurrentUser && <span className="trainers-you-badge">{t('trainersList.you')}</span>}
                                            </td>
                                            <td className="trainers-centered-column">{t_item.email || '---'}</td>
                                            <td className="trainers-centered-column">{t_item.phone || '---'}</td>
                                            <td className="trainers-centered-column">{formatAddress(t_item.address)}</td>
                                            <td className="trainers-centered-column">
                                                <span className={`trainers-role-badge trainers-role-${t_item.trainerSchoolRole?.toLowerCase()}`}>
                                                    {getRoleDisplayName(t_item.trainerSchoolRole)}
                                                </span>
                                            </td>
                                            <td className="trainers-centered-column">
                                                {canRemove && (
                                                    <button
                                                        className="trainers-delete-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDeleteModal(t_item);
                                                        }}
                                                    >
                                                        {t('common:delete')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="trainers-mobile-cards">
                        {trainers.length === 0 ? (
                            <div className="trainers-mobile-no-data">
                                {t('trainersList.noTrainers')}
                            </div>
                        ) : (
                            trainers.map((t_item) => {
                                const fullName =
                                    t_item.firstName && t_item.lastName
                                        ? `${t_item.firstName} ${t_item.lastName}`
                                        : t_item.username;

                                const isCurrentUser = trainer?.id === t_item.id;
                                const canRemove = !isCurrentUser && canRemoveTrainer(trainer?.trainerSchoolRole, t_item.trainerSchoolRole);

                                return (
                                    <div
                                        key={t_item.id}
                                        className="trainers-card"
                                    >
                                        <div className="trainers-card-header">
                                            <h3 className="trainers-name">
                                                {fullName}
                                                {isCurrentUser && <span className="trainers-you-badge">{t('trainersList.you')}</span>}
                                            </h3>
                                            <span className={`trainers-role-badge trainers-role-${t_item.trainerSchoolRole?.toLowerCase()}`}>
                                                {getRoleDisplayName(t_item.trainerSchoolRole)}
                                            </span>
                                        </div>
                                        <div className="trainers-details">
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">{t('trainersList.email')}</span>
                                                <span className="trainers-detail-value">{t_item.email || '---'}</span>
                                            </div>
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">{t('trainersList.phone')}</span>
                                                <span className="trainers-detail-value">{t_item.phone || '---'}</span>
                                            </div>
                                            <div className="trainers-detail-item">
                                                <span className="trainers-detail-label">{t('trainersList.address')}</span>
                                                <span className="trainers-detail-value">{formatAddress(t_item.address)}</span>
                                            </div>
                                        </div>
                                        {canRemove && (
                                            <div className="trainers-card-actions">
                                                <button
                                                    className="trainers-delete-button-mobile"
                                                    onClick={() => openDeleteModal(t_item)}
                                                >
                                                    {t('trainersList.removeTrainer')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}

            <InviteTrainerModal isOpen={isModalOpen} onClose={closeModal} />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteTrainer}
                itemName={trainerToDelete ?
                    (trainerToDelete.firstName && trainerToDelete.lastName
                        ? `${trainerToDelete.firstName} ${trainerToDelete.lastName}`
                        : trainerToDelete.username)
                    : ''
                }
            />
        </div>
    );
};

export default TrainersList;
